/**
 * Samen spelen: vrienden, een kamer maken, meedoen met een code.
 *
 * Gewone HTML, net als het inlogscherm en om dezelfde reden: hier wordt getypt en gelezen,
 * en dat doet de browser beter dan een canvas. Het bord blijft canvas.
 *
 * Wat dit scherm bewust *niet* doet is spelregels kennen. Het vraagt de server om een
 * kamer, laat zien wie erin zit, en geeft door wanneer er gestart wordt. Wie er begint,
 * of een figuur nog vrij is, of een code nog werkt — dat beslist allemaal de server, want
 * dat zijn de vragen waar twee browsers het oneens over kunnen zijn.
 *
 * En net als `netpotje.ts`: het netwerk is geen randgeval. Elke knop kan op een dode
 * verbinding drukken, en dan hoort er een melding te staan — geen klik die stil niets
 * doet. Het patroon (bezig-vlag, meldingen) komt uit `inlogscherm.ts`.
 */
import type { Karakter } from '@catan/contracts';

const STIJL = `
#samen {
  position: absolute; inset: 0; z-index: 20; display: grid; place-items: center;
  background: rgba(12, 7, 2, 0.86);
}
#samen .kader {
  width: min(34rem, 94vw); max-height: 90vh; overflow: auto; padding: 1.2rem 1.4rem;
  background: #17100a; color: #f3e6cf;
  border: 2px solid #c9a227; border-radius: 6px;
  font-family: Georgia, 'Times New Roman', serif;
}
#samen h2 { margin: 0 0 0.2rem; font-size: 1.1rem; color: #ffd257; }
#samen h3 { margin: 1rem 0 0.3rem; font-size: 0.95rem; color: #ffd257; }
#samen p { margin: 0.3rem 0; font-size: 0.85rem; color: #d8c9a8; }
#samen ul { list-style: none; margin: 0.3rem 0; padding: 0; }
#samen li {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.25rem 0; border-bottom: 1px dotted #6b5a3e; font-size: 0.85rem;
}
#samen li .naam { flex: 1; }
#samen .lamp { width: 0.6rem; height: 0.6rem; border-radius: 50%; background: #6b5a3e; }
#samen .lamp.aan { background: #a9dc8f; }
#samen input, #samen select {
  padding: 0.35rem 0.5rem; font-family: inherit; font-size: 0.9rem;
  background: #f3e6cf; color: #2b1d0e; border: 2px solid #7a5c33; border-radius: 4px;
}
#samen .rij { display: flex; gap: 0.5rem; margin: 0.4rem 0; }
#samen .rij input, #samen .rij select { flex: 1; min-width: 0; }
#samen button {
  font-family: inherit; font-size: 0.85rem; padding: 0.3rem 0.6rem;
  background: #e3cfa3; border: 2px solid #7a5c33; border-radius: 4px;
  cursor: pointer; color: #2b1d0e;
}
#samen button.zwak { background: #2c1608; color: #e3cfa3; }
#samen button:disabled { opacity: 0.55; cursor: default; }
#samen .code {
  font-family: 'DejaVu Sans Mono', monospace; font-size: 1.6rem; letter-spacing: 0.3rem;
  color: #ffd257; text-align: center; margin: 0.6rem 0;
}
#samen .link {
  font-family: 'DejaVu Sans Mono', monospace; color: #ffd257; word-break: break-all;
}
#samen .melding { min-height: 1.2rem; font-size: 0.85rem; color: #ffb4a0; }
`;

interface Vriend {
  id: string;
  gebruikersnaam: string;
  stand: 'pending' | 'accepted' | 'blocked';
  wachtOpJou: boolean;
  online: boolean;
}

interface Kamer {
  id: string;
  code?: string;
  status: string;
  stoelen: { seat: number; userId: string | null; naam: string; figuur: string | null }[];
}

/**
 * Roept de server aan. Geeft altijd een object terug — bij netwerkuitval één met een
 * `bericht`, zodat elke knop hetzelfde pad heeft voor "het lukte niet".
 */
async function api(pad: string, lichaam?: unknown): Promise<Record<string, unknown>> {
  let res: Response;
  try {
    res = await fetch(pad, {
      method: lichaam === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      ...(lichaam === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(lichaam) }),
    });
  } catch {
    return { bericht: 'geen verbinding met de server; probeer het zo nog eens' };
  }
  return ((await res.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
}

/** Zet tekst veilig in HTML. De namen komen van de server; vertrouwen is geen invoerfilter. */
function veilig(tekst: string): string {
  return tekst.replace(/[&<>"']/g, (t) => `&#${t.charCodeAt(0)};`);
}

export interface SamenOpties {
  readonly figuren: readonly Karakter[];
  /**
   * Een code die al bekend is, bijvoorbeeld uit een gedeelde link.
   *
   * Dan hoeft niemand hem over te typen — en overtypen is precies waar `0` en `O` door
   * elkaar gaan, ook al zit er maar één van de twee in het alfabet.
   */
  readonly code?: string | undefined;
  /** Aangeroepen als er een potje begint waar jij in zit. */
  readonly onPotje: (gameId: string, figuur: string) => void;
  readonly onSluiten: () => void;
}

/** Zet het scherm neer. Geeft een functie terug die het weghaalt. */
export function samenscherm(opties: SamenOpties): () => void {
  if (document.getElementById('samen-stijl') === null) {
    const stijl = document.createElement('style');
    stijl.id = 'samen-stijl';
    stijl.textContent = STIJL;
    document.head.appendChild(stijl);
  }

  const laag = document.createElement('div');
  laag.id = 'samen';
  document.body.appendChild(laag);

  let vrienden: Vriend[] = [];
  let kamer: Kamer | undefined;
  let melding = '';
  /**
   * Er is een verzoek onderweg. Eén tegelijk: een dubbelklik op "kamer maken" leverde
   * anders twéé kamers op, waarvan er één als wees op de server achterbleef.
   */
  let bezig = false;
  /**
   * Het figuur dat jij koos toen je de kamer maakte of eraan meedeed.
   *
   * Apart bijgehouden, want de kamer vertelt wel welke figuren er aan tafel zitten maar
   * niet welk daarvan van jou is — dat weet alleen deze browser.
   */
  let gekozenFiguur = opties.figuren[0]?.portret ?? 'louis';
  let timer: number | undefined;
  let gesloten = false;
  let poluitval = 0;

  const weg = (): void => {
    gesloten = true;
    if (timer !== undefined) window.clearInterval(timer);
    laag.remove();
  };

  const mijnFiguur = (): string =>
    laag.querySelector<HTMLSelectElement>('#samen-figuur')?.value ?? gekozenFiguur;

  const haalVrienden = async (): Promise<void> => {
    const uit = await api('/api/vrienden');
    vrienden = (uit['vrienden'] as Vriend[] | undefined) ?? [];
  };

  /** Wat er op het scherm van afhangt; verandert dit, dan wordt er opnieuw getekend. */
  const kamerHash = (k: Kamer | undefined): string =>
    k === undefined
      ? ''
      : `${k.status}|${k.stoelen.map((s) => `${s.seat}:${s.naam}:${s.figuur ?? ''}`).join('|')}`;

  /**
   * Kijkt of er iets in de kamer veranderd is.
   *
   * Elke twee seconden, zolang je in een kamer zit die nog wacht. Herrenderen gebeurt op
   * de hele kamerstand — eerst alleen op het aantal stoelen, en dan zag je een gewisseld
   * figuur nooit. Valt de kamer weg (opgeheven, verlopen), dan hoor je dat ook.
   */
  const volgKamer = (): void => {
    if (timer !== undefined) window.clearInterval(timer);
    if (kamer === undefined || gesloten) return;
    timer = window.setInterval(() => {
      void (async () => {
        if (kamer === undefined || gesloten) return;
        const uit = await api(`/api/kamer?id=${encodeURIComponent(kamer.id)}`);
        const nieuw = uit['kamer'] as Kamer | undefined;
        if (nieuw === undefined) {
          // Even geen antwoord is geen ramp; drie keer op rij wel een mededeling waard.
          // En een kamer die echt weg is (de maker hief hem op), is een terugkeer naar af.
          poluitval += 1;
          if (typeof uit['fout'] === 'string' && uit['fout'] === 'KAMER_ONBEKEND') {
            kamer = undefined;
            if (timer !== undefined) window.clearInterval(timer);
            melding = 'De kamer bestaat niet meer.';
            teken();
          } else if (poluitval === 3) {
            zeg('De verbinding hapert; we blijven het proberen…');
          }
          return;
        }
        poluitval = 0;
        // Is het potje begonnen — ook als de ánder op start drukte — dan gaan we spelen.
        if (nieuw.status === 'active') {
          weg();
          opties.onPotje(nieuw.id, gekozenFiguur);
          return;
        }
        if (nieuw.status !== 'lobby') {
          kamer = undefined;
          if (timer !== undefined) window.clearInterval(timer);
          melding = 'De kamer is opgeheven.';
          teken();
          return;
        }
        if (kamerHash(nieuw) !== kamerHash(kamer)) {
          kamer = nieuw;
          teken();
        }
      })();
    }, 2000);
  };

  const zeg = (tekst: string): void => {
    melding = tekst;
    const vak = laag.querySelector('.melding');
    if (vak !== null) vak.textContent = tekst;
  };

  /**
   * Eén verzoek tegelijk, met de knoppen op slot zolang het loopt.
   *
   * De sloten zijn zichtbaar (disabled), zodat een trage server eruitziet als een trage
   * server en niet als een kapotte knop.
   */
  const doe = (werk: () => Promise<void>): void => {
    if (bezig) return;
    bezig = true;
    for (const knop of laag.querySelectorAll('button')) knop.disabled = true;
    void werk().finally(() => {
      bezig = false;
      if (!gesloten) for (const knop of laag.querySelectorAll('button')) knop.disabled = false;
    });
  };

  const teken = (): void => {
    // Wat de gebruiker aan het typen was, overleeft het hertekenen. De poll tekent
    // opnieuw zodra er iemand bij de kamer komt — precies het moment waarop jij nog
    // half een gebruikersnaam in het vriendenvak hebt staan.
    const bewaarCode = laag.querySelector<HTMLInputElement>('#samen-code')?.value;
    const bewaarVriend = laag.querySelector<HTMLInputElement>('#samen-vriend')?.value;

    const figuren = opties.figuren
      .map(
        (k) =>
          `<option value="${k.portret}"${k.portret === gekozenFiguur ? ' selected' : ''}>${veilig(k.naam)}</option>`,
      )
      .join('');
    const figuurNaam = (portret: string | null): string =>
      veilig(opties.figuren.find((k) => k.portret === portret)?.naam ?? portret ?? '');

    laag.innerHTML = `
      <div class="kader">
        <h2>Samen spelen</h2>
        <p>Speel tegen iemand anders. Jullie hebben allebei een account nodig.</p>

        <h3>Jouw speelfiguur</h3>
        <div class="rij">
          <select id="samen-figuur" ${kamer === undefined ? '' : 'disabled'}>${figuren}</select>
        </div>

        ${
          kamer === undefined
            ? `
        <h3>Een kamer maken</h3>
        <div class="rij"><button id="samen-maak">Kamer maken en code delen</button></div>

        <h3>Meedoen met een code</h3>
        <div class="rij">
          <input id="samen-code" maxlength="6" placeholder="ABC234" autocapitalize="characters"
                 spellcheck="false" value="${veilig(bewaarCode ?? opties.code ?? '')}" />
          <button id="samen-mee">Meedoen</button>
        </div>`
            : `
        <h3>Jouw kamer</h3>
        ${
          kamer.code === undefined
            ? ''
            : `<div class="code">${veilig(kamer.code)}</div>
               <p>Of stuur deze link: <span class="link">${window.location.origin}/mee/${veilig(kamer.code)}</span></p>`
        }
        <p>${
          kamer.stoelen.length < 2
            ? 'Geef deze code aan degene met wie je wilt spelen.'
            : 'Iedereen is er. Je kunt beginnen.'
        }</p>
        <ul>${kamer.stoelen
          .map(
            (s) =>
              `<li><span class="naam">${veilig(s.naam)}</span><span>${figuurNaam(s.figuur)}</span></li>`,
          )
          .join('')}</ul>
        <div class="rij">
          <button id="samen-start" ${kamer.stoelen.length < 2 ? 'disabled' : ''}>Beginnen</button>
          <button id="samen-verlaat" class="zwak">Kamer verlaten</button>
        </div>`
        }

        <h3>Vrienden</h3>
        <div class="rij">
          <input id="samen-vriend" placeholder="gebruikersnaam" spellcheck="false"
                 value="${veilig(bewaarVriend ?? '')}" />
          <button id="samen-vriendtoe">Toevoegen</button>
        </div>
        <ul id="samen-vrienden">${
          vrienden.length === 0
            ? '<li><span class="naam">Nog geen vrienden.</span></li>'
            : vrienden
                .map(
                  (v) => `<li>
                    <span class="lamp ${v.online ? 'aan' : ''}"></span>
                    <span class="naam">${veilig(v.gebruikersnaam)}</span>
                    ${
                      v.stand === 'pending'
                        ? v.wachtOpJou
                          ? `<button data-ja="${veilig(v.id)}">Ja</button><button data-nee="${veilig(v.id)}" class="zwak">Nee</button>`
                          : '<span>gevraagd</span>'
                        : '<span>vriend</span>'
                    }
                  </li>`,
                )
                .join('')
        }</ul>

        <p class="melding" role="status" aria-live="polite">${veilig(melding)}</p>
        <div class="rij"><button id="samen-dicht" class="zwak">Sluiten</button></div>
      </div>`;

    laag.querySelector('#samen-dicht')?.addEventListener('click', () => {
      weg();
      opties.onSluiten();
    });

    laag.querySelector('#samen-maak')?.addEventListener('click', () => {
      doe(async () => {
        gekozenFiguur = mijnFiguur();
        // Zelf verdelen is online de standaard: je begint met een leeg bord, precies als
        // tegen de computer — en wie geen zin heeft klikt in het potje op "Verdeel voor mij".
        const uit = await api('/api/kamers', { figuur: gekozenFiguur, zelfVerdelen: true });
        if (typeof uit['id'] !== 'string') {
          zeg(String(uit['bericht'] ?? 'de kamer kon niet gemaakt worden'));
          return;
        }
        const gelezen = await api(`/api/kamer?id=${encodeURIComponent(uit['id'])}`);
        kamer = gelezen['kamer'] as Kamer | undefined;
        melding = '';
        teken();
        volgKamer();
      });
    });

    laag.querySelector('#samen-mee')?.addEventListener('click', () => {
      doe(async () => {
        const code = laag.querySelector<HTMLInputElement>('#samen-code')?.value.trim() ?? '';
        gekozenFiguur = mijnFiguur();
        const uit = await api('/api/kamer/meedoen', {
          code: code.toUpperCase(),
          figuur: gekozenFiguur,
          zelfVerdelen: true,
        });
        if (typeof uit['id'] !== 'string') {
          zeg(String(uit['bericht'] ?? 'meedoen lukte niet'));
          return;
        }
        const gelezen = await api(`/api/kamer?id=${encodeURIComponent(uit['id'])}`);
        kamer = gelezen['kamer'] as Kamer | undefined;
        melding = 'Je zit in de kamer. Wacht tot er gestart wordt.';
        teken();
        volgKamer();
      });
    });

    laag.querySelector('#samen-start')?.addEventListener('click', () => {
      doe(async () => {
        if (kamer === undefined) return;
        const uit = await api(`/api/kamer/start?id=${encodeURIComponent(kamer.id)}`, {});
        if (typeof uit['id'] !== 'string') {
          zeg(String(uit['bericht'] ?? 'starten lukte niet'));
          return;
        }
        const id = uit['id'];
        weg();
        opties.onPotje(id, gekozenFiguur);
      });
    });

    laag.querySelector('#samen-verlaat')?.addEventListener('click', () => {
      doe(async () => {
        // Eerst de server, dan pas het scherm. Alleen lokaal "teruggaan" liet je stoel
        // bezet achter, en dan startte de ander een potje tegen een lege stoel.
        if (kamer !== undefined) {
          const uit = await api(`/api/kamer/verlaten?id=${encodeURIComponent(kamer.id)}`, {});
          if (uit['verlaten'] !== true && typeof uit['bericht'] === 'string') {
            zeg(uit['bericht']);
            return;
          }
        }
        kamer = undefined;
        if (timer !== undefined) window.clearInterval(timer);
        melding = '';
        teken();
      });
    });

    laag.querySelector('#samen-vriendtoe')?.addEventListener('click', () => {
      doe(async () => {
        const naam = laag.querySelector<HTMLInputElement>('#samen-vriend')?.value.trim() ?? '';
        if (naam === '') return;
        const uit = await api('/api/vrienden', { gebruikersnaam: naam });
        if (uit['vrienden'] === undefined) {
          zeg(String(uit['bericht'] ?? 'dat lukte niet'));
          return;
        }
        vrienden = uit['vrienden'] as Vriend[];
        melding = 'Verzoek verstuurd.';
        teken();
      });
    });

    for (const [attribuut, antwoord] of [
      ['ja', 'accepteren'],
      ['nee', 'weigeren'],
    ] as const) {
      for (const knop of laag.querySelectorAll<HTMLElement>(`[data-${attribuut}]`)) {
        knop.addEventListener('click', () => {
          doe(async () => {
            const uit = await api('/api/vrienden/antwoord', {
              id: knop.dataset[attribuut],
              antwoord,
            });
            if (uit['vrienden'] !== undefined) vrienden = uit['vrienden'] as Vriend[];
            teken();
          });
        });
      }
    }
  };

  teken();
  void haalVrienden().then(() => {
    if (!gesloten) teken();
  });
  return weg;
}
